/**
 * 送信先の一覧 `/dlp/upload/domains`(docs/dlp-upload.md §10)。アップロード検知で当てるドメインと、その扱い(監視 / 遮断 / 除外)。
 * 一覧(行を押すと編集)+「送信先を追加」。編集は dlp:admin、表示は viewer。builtin(同梱)は削除できず、ドメインも変えられない。
 * 保存すると全端末の設定版が上がり、次の poll で届く。
 */
import { useMutation } from "@tanstack/react-query";
import { Alert, Button, Form, Input, Modal, Popconfirm, Select, Space, Switch, Tag, Typography, message } from "antd";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import type { DlpUploadDomain, DlpUploadDomainInput } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import {
  DataTable,
  FilterBar,
  PageHeader,
  PopClose,
  StatusTag,
  TimeAgo,
  useSearchParamInput,
  useSearchParamsPatch,
  type ConditionDef,
  type DataColumn,
} from "../../ui";
import { uploadActionTag } from "./bits";
import { UPLOAD_ACTIONS, UPLOAD_CATEGORIES, uploadActionLabel, uploadCategoryLabel } from "./constants";
import { errorText, useDlpPerms, useDlpUploadDomains, useInvalidateDlp } from "./hooks";

const { Text } = Typography;

/** action=block を選んだときに出す注意(§10)。遮断はブラウザのポリシーだけで効く(§6) */
const BLOCK_WARNING =
  "ブラウザだけで止まる。同期アプリは止まらない。設定の『ブラウザで遮断する』が有効なときだけ効く";

export function UploadDomainsPage() {
  const { canView, canAdmin, hasUpload } = useDlpPerms();
  const [sp] = useSearchParams();
  const patch = useSearchParamsPatch();
  const [qInput, setQInput] = useSearchParamInput("q");
  const q = sp.get("q") ?? "";
  const category = sp.get("category") ?? "";
  const action = sp.get("action") ?? "";
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  // null = 閉じている / "new" = 追加 / DlpUploadDomain = 編集(または表示)
  const [editing, setEditing] = useState<DlpUploadDomain | "new" | null>(null);

  const filters: Record<string, string> = {
    ...(q ? { q } : {}),
    ...(category ? { category } : {}),
    ...(action ? { action } : {}),
  };
  // 門はルート(RequireFeature)にもあるが、問い合わせも機能・ロールに合わせる(契約外の API を叩かない)
  const query = useDlpUploadDomains(
    { ...filters, limit: String(pageSize), offset: String((page - 1) * pageSize) },
    hasUpload && canView,
  );
  const filterKey = `${q}|${category}|${action}`;
  useEffect(() => setPage(1), [filterKey]);
  const rows = query.data?.items ?? [];

  const columns: DataColumn<DlpUploadDomain>[] = [
    {
      title: "ドメイン",
      ellipsis: false,
      width: 260,
      render: (_: unknown, d) => (
        <span>
          <span className="ui-num">{d.domain}</span> {d.builtin && <Tag>同梱</Tag>}
        </span>
      ),
    },
    { title: "カテゴリ", ellipsis: false, width: 160, render: (_: unknown, d) => uploadCategoryLabel(d.category) },
    { title: "扱い", ellipsis: false, width: 140, render: (_: unknown, d) => uploadActionTag(d.action) },
    {
      title: "有効",
      ellipsis: false,
      width: 110,
      render: (_: unknown, d) =>
        d.enabled ? <StatusTag tone="success">有効</StatusTag> : <StatusTag tone="neutral">無効</StatusTag>,
    },
    { title: "メモ", ellipsis: true, onCell: (row) => ({ title: String(row.note ?? "") }), dataIndex: "note", width: 320, render: (v: string) => <span className="ui-cell-sub">{v}</span> },
    { title: "更新", ellipsis: false, dataIndex: "updated_at", width: 160, render: (v: string) => <TimeAgo iso={v} /> },
  ];

  const selectCondition = (
    key: "category" | "action",
    label: string,
    value: string,
    options: { value: string; label: string }[],
    toLabel: (v: string) => string,
  ): ConditionDef => ({
    key,
    label,
    value: value ? toLabel(value) : "",
    onClear: () => patch({ [key]: null }, true),
    control: (close) => (
      <>
        <Select
          autoFocus
          style={{ minWidth: 180 }}
          value={value || undefined}
          placeholder={label}
          options={options}
          onChange={(v) => {
            patch({ [key]: v ?? null }, true);
            close();
          }}
        />
        <PopClose onClick={close} />
      </>
    ),
  });
  const conditions = [
    selectCondition("category", "カテゴリ", category, UPLOAD_CATEGORIES, uploadCategoryLabel),
    selectCondition("action", "扱い", action, UPLOAD_ACTIONS, uploadActionLabel),
  ];
  const filtered = Object.keys(filters).length > 0;
  const clearAll = () => {
    setQInput("");
    patch({ q: null, category: null, action: null }, true);
  };

  return (
    <div className="ui-list">
      <div className="ui-list-main">
        <PageHeader
          title="送信先の一覧"
          description="アップロード検知で当てるドメイン。下位名にも当たり、最も長く一致した行が効く。除外(許可)はより短い行を打ち消す(自社テナントなど)。遮断はブラウザのポリシーだけで効き、同期アプリは止めない。"
          primary={
            canAdmin ? (
              <Button type="primary" onClick={() => setEditing("new")}>
                送信先を追加
              </Button>
            ) : undefined
          }
        />
        <FilterBar
          search={{ value: qInput, onChange: setQInput, placeholder: "ドメイン / メモ" }}
          conditions={conditions}
          right={
            filtered ? (
              <Button size="small" onClick={clearAll}>
                条件を外す
              </Button>
            ) : undefined
          }
        />
        {query.isError && (
          <Alert type="error" showIcon message="送信先の一覧を取得できなかった" description={errorText(query.error)} />
        )}
        <DataTable<DlpUploadDomain>
          columns={columns}
          scrollX={columns.reduce((sum, c) => sum + c.width, 0)}
          rows={rows}
          rowKey={(r) => r.id}
          loading={query.isFetching}
          total={query.data?.total ?? 0}
          page={page}
          pageSize={pageSize}
          onPage={(p, s) => {
            setPage(p);
            setPageSize(s);
          }}
          onRowClick={(row) => setEditing(row)}
          rowTone={(row) => (row.enabled ? undefined : "neutral")}
          error={query.isError ? "取得できなかったので表示できない" : undefined}
          empty={{
            text: filtered ? "条件に合う送信先はない" : "送信先がない(同梱の行はマイグレーションでシードされる)",
            action: filtered ? <Button onClick={clearAll}>条件を外す</Button> : undefined,
          }}
        />
      </div>
      <DomainForm
        open={editing !== null}
        domain={editing === "new" ? null : editing}
        readOnly={!canAdmin}
        onClose={() => setEditing(null)}
      />
    </div>
  );
}

function DomainForm({
  open,
  domain,
  readOnly,
  onClose,
}: {
  open: boolean;
  domain: DlpUploadDomain | null;
  readOnly: boolean;
  onClose: () => void;
}) {
  const api = useApi();
  const invalidate = useInvalidateDlp();
  const [form] = Form.useForm<DlpUploadDomainInput>();
  const action = Form.useWatch("action", form);

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue(
      domain
        ? { domain: domain.domain, category: domain.category, action: domain.action, enabled: domain.enabled, note: domain.note }
        : { domain: "", category: "storage", action: "monitor", enabled: true, note: "" },
    );
  }, [open, domain, form]);

  const save = useMutation({
    mutationFn: (v: DlpUploadDomainInput) => api.dlpSaveUploadDomain(domain?.id ?? null, v),
    onSuccess: () => {
      void message.success("保存した(監査ログに記録した。端末には次の poll で届く)");
      invalidate();
      onClose();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const remove = useMutation({
    mutationFn: () => api.dlpDeleteUploadDomain(domain!.id),
    onSuccess: () => {
      void message.success("削除した(監査ログに記録した)");
      invalidate();
      onClose();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const title = readOnly ? "送信先" : domain ? "送信先を編集" : "送信先を追加";
  return (
    <Modal
      open={open}
      title={title}
      onCancel={onClose}
      footer={
        readOnly ? (
          <Space>
            <Text type="secondary">変更は dlp:admin</Text>
            <Button onClick={onClose}>閉じる</Button>
          </Space>
        ) : (
          <Space>
            {domain && !domain.builtin && (
              <Popconfirm
                title="この送信先を削除する"
                description="次の poll で端末から外れる"
                okText="削除"
                cancelText="やめる"
                okButtonProps={{ danger: true }}
                onConfirm={() => remove.mutate()}
              >
                <Button danger loading={remove.isPending}>
                  削除
                </Button>
              </Popconfirm>
            )}
            <Button onClick={onClose}>やめる</Button>
            <Button type="primary" loading={save.isPending} onClick={() => form.submit()}>
              保存
            </Button>
          </Space>
        )
      }
    >
      <Form<DlpUploadDomainInput>
        form={form}
        layout="vertical"
        disabled={readOnly || save.isPending}
        onFinish={(v) => save.mutate({ ...v, domain: v.domain.trim(), note: v.note ?? "" })}
      >
        <Form.Item
          name="domain"
          label="ドメイン"
          rules={[{ required: true, whitespace: true, message: "ドメインを入れる" }]}
          extra={
            domain?.builtin
              ? "同梱の行はドメインを変えられない(扱い・有効・メモは変えられる)"
              : "例: dropbox.com(下位名にも当たる)。* は使えない。公開接尾辞(co.jp など)だけのものは保存できない"
          }
        >
          <Input disabled={readOnly || !!domain?.builtin} placeholder="example.com" />
        </Form.Item>
        <Space size={16} wrap align="start">
          <Form.Item name="category" label="カテゴリ" rules={[{ required: true }]}>
            <Select style={{ width: 200 }} options={UPLOAD_CATEGORIES} />
          </Form.Item>
          <Form.Item name="action" label="扱い" rules={[{ required: true }]}>
            <Select style={{ width: 200 }} options={UPLOAD_ACTIONS} />
          </Form.Item>
          <Form.Item name="enabled" label="有効" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Space>
        {action === "block" && (
          <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={BLOCK_WARNING} />
        )}
        {action === "allow" && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="このドメインとその下位名は検知しない(より短い行を打ち消す。自社テナントの除外に使う)"
          />
        )}
        <Form.Item name="note" label="メモ">
          <Input.TextArea rows={2} maxLength={500} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
